import { FolderContent } from '@/api/generatedApi';
import { Alert, Box, LinearProgress, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material';
import FolderIcon from '@mui/icons-material/Folder';
import HomeIcon from '@mui/icons-material/Home';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { ContentIcon, StatusIcon } from './ContentIcons';
import { ContentMoveState, getContentName } from './utils';

export const MoveConfirmationContent = ({
  sortedContents,
  moveStates,
  targetFolderName,
  isMovingToRoot,
  isProcessing,
  completedCount,
  progress,
}: {
  sortedContents: FolderContent[];
  moveStates: ContentMoveState[];
  targetFolderName: string;
  isMovingToRoot: boolean;
  isProcessing: boolean;
  completedCount: number;
  progress: number;
}) => (
  <>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        mb: 2,
      }}
    >
      <Box
        sx={{
          flex: 1,
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: '8px',
          maxHeight: 220,
          overflow: 'auto',
        }}
      >
        <List dense disablePadding>
          {sortedContents.map((content, index) => {
            const moveState = moveStates.find(s => s.content.id === content.id);
            return (
              <ListItem
                key={content.id}
                sx={{
                  borderBottom: index < sortedContents.length - 1 ? '1px solid' : 'none',
                  borderColor: 'divider',
                  py: 0.75,
                  minHeight: 36,
                }}
              >
                <ListItemIcon sx={{ minWidth: 32 }}>
                  <ContentIcon content={content} size={18} />
                </ListItemIcon>
                <ListItemText
                  primary={getContentName(content)}
                  slotProps={{
                    primary: {
                      variant: 'body2',
                      noWrap: true,
                      fontSize: '0.8125rem',
                      sx: {
                        color: moveState?.status === 'error' ? 'error.main' : 'text.primary',
                      },
                    },
                  }}
                />
                {moveState && (
                  <Box sx={{ ml: 1, display: 'flex', alignItems: 'center' }}>
                    <StatusIcon status={moveState.status} />
                  </Box>
                )}
              </ListItem>
            );
          })}
        </List>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 1,
        }}
      >
        <ArrowForwardIcon sx={{ fontSize: 28, color: 'text.secondary' }} />
      </Box>
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '8px',
          backgroundColor: 'action.hover',
          p: 2,
          minHeight: 120,
        }}
      >
        {isMovingToRoot ? (
          <HomeIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        ) : (
          <FolderIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
        )}
        <Typography
          variant='body2'
          fontWeight={500}
          textAlign='center'
          sx={{
            wordBreak: 'break-word',
            maxWidth: '100%',
          }}
        >
          {targetFolderName}
        </Typography>
      </Box>
    </Box>
    {moveStates.some(s => s.status === 'error') && (
      <Alert severity='error' sx={{ mb: 2 }}>
        {moveStates
          .filter(s => s.status === 'error')
          .map(s => (
            <Typography key={s.content.id} variant='body2' display='block'>
              {getContentName(s.content)}: {s.error}
            </Typography>
          ))}
      </Alert>
    )}
    {isProcessing && (
      <Box sx={{ width: '100%' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant='caption' color='textSecondary'>
            Moving {completedCount} of {moveStates.length}
          </Typography>
          <Typography variant='caption' color='textSecondary'>
            {Math.round(progress)}%
          </Typography>
        </Box>
        <LinearProgress variant='determinate' value={progress} sx={{ borderRadius: 1 }} />
      </Box>
    )}
  </>
);
